import { TileMap } from "@/game/map/TileMap";
import { Familiar, Direction } from "@/game/entities/Familiar";
import { Treasure } from "@/game/entities/Treasure";
import { ThemeName } from "@/game/map/ThemeConfig";
import { TileMapRenderer } from "./TileMapRenderer";
import { FamiliarRenderer } from "./FamiliarRenderer";
import { TreasureRenderer } from "./TreasureRenderer";
import "./field.css";

/**
 * ステージデータの型（PlayScene.StageDataと同一）
 */
export interface StageData {
  familiar: {
    x: number;
    y: number;
    direction: Direction;
  };
  treasure: {
    x: number;
    y: number;
  };
  walls?: { x: number; y: number }[];
  theme?: ThemeName;
  stageId?: number;
}

/**
 * ゲーム描画の統合クラス（PlaySceneの代替）
 *
 * ステージデータを受け取り、ロジック層のインスタンスを生成し、
 * CSS/DOMで描画する。render()を呼ぶたびにDOMを再構築する。
 *
 * Executorへの橋渡し: getFamiliar() / getTileMap() / getTreasure()
 */
export class GameRenderer {
  private container: HTMLElement;
  private tileMap: TileMap | null = null;
  private familiar: Familiar | null = null;
  private treasure: Treasure | null = null;
  private tileMapRenderer: TileMapRenderer | null = null;
  private familiarRenderer: FamiliarRenderer | null = null;
  private treasureRenderer: TreasureRenderer | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  /**
   * ステージを描画（再描画）
   *
   * DOMをクリアして再構築する。PlayScene.restart() に相当。
   * DOM操作は同期的なので非同期待機は不要。
   */
  render(data: StageData): void {
    // DOMクリア
    this.container.replaceChildren();

    const theme = data.theme ?? "cave";
    const stageId = data.stageId ?? 1;

    // ロジック層を生成
    this.tileMap = new TileMap(theme);
    if (data.walls) {
      for (const wall of data.walls) {
        this.tileMap.setTile(wall.x, wall.y, "wall");
      }
    }

    this.treasure = new Treasure(data.treasure.x, data.treasure.y);
    this.familiar = new Familiar(
      data.familiar.x,
      data.familiar.y,
      data.familiar.direction
    );

    // 描画層を生成
    this.tileMapRenderer = new TileMapRenderer(this.tileMap, stageId);
    this.treasureRenderer = new TreasureRenderer(this.treasure);
    this.familiarRenderer = new FamiliarRenderer(this.familiar);

    // ゴーレム移動時のDOM付け替えを接続
    this.familiarRenderer.onMove = (gridX, gridY) => {
      this.tileMapRenderer!.moveEntity(this.familiarRenderer!, gridX, gridY);
    };

    // DOMツリーに追加
    this.container.appendChild(this.tileMapRenderer!.getElement());

    // エンティティをタイルに配置（宝箱→ゴーレムの順 = z-index でゴーレムが上）
    this.tileMapRenderer!.placeEntity(
      this.treasureRenderer!,
      this.treasure!.gridX,
      this.treasure!.gridY
    );
    this.tileMapRenderer!.placeEntity(
      this.familiarRenderer!,
      this.familiar!.gridX,
      this.familiar!.gridY
    );
  }

  getFamiliar(): Familiar {
    if (!this.familiar) {
      throw new Error(
        "GameRenderer: render() must be called before getFamiliar()"
      );
    }
    return this.familiar;
  }

  getTileMap(): TileMap {
    if (!this.tileMap) {
      throw new Error(
        "GameRenderer: render() must be called before getTileMap()"
      );
    }
    return this.tileMap;
  }

  getTreasure(): Treasure {
    if (!this.treasure) {
      throw new Error(
        "GameRenderer: render() must be called before getTreasure()"
      );
    }
    return this.treasure;
  }
}
